import { tKeys, useTranslation } from 'locales';
import { mapValues, pipe } from 'remeda';
import type { ApplicationLocale } from 'types';

import type { UpsellFeature } from '../types';

type FeatureDefinition = {
  title: string;
  description: string;
  mediaSrc: string;
};

type FeatureDefinitionWithLocales = Omit<FeatureDefinition, 'mediaSrc'> & {
  mediaSrcByLocale: Record<ApplicationLocale, string>;
};

export const useBuildUpsellFeature = () => {
  const { t, currentLang } = useTranslation();

  const featureDefinitions = pipe(
    FEATURE_DEFINITION,
    mapValues(feature => ({
      ...feature,
      title: t(feature.title),
      description: t(feature.description),
      mediaSrc: getMediaSrcForLocale(feature.mediaSrcByLocale, currentLang),
    })),
  );

  return (featureName: UpsellFeature) => featureDefinitions[featureName];
};

function getMediaSrcForLocale(
  mediaSrcByLocale: Record<ApplicationLocale, string>,
  currentLocale: ApplicationLocale,
): string {
  return mediaSrcByLocale[currentLocale] ?? mediaSrcByLocale.fr;
}

const FEATURE_DEFINITION = {
  automations: {
    title: tKeys.UPSELL.AUTOMATIONS.TITLE,
    description: tKeys.UPSELL.AUTOMATIONS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/automations-fr.mp4',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/automations-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/automations-nl.png',
    },
  },
  tasks: {
    title: tKeys.UPSELL.TASKS.TITLE,
    description: tKeys.UPSELL.TASKS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/tasks-fr.png',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/tasks-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/tasks-nl.png',
    },
  },
  analytics: {
    title: tKeys.UPSELL.ANALYTICS.TITLE,
    description: tKeys.UPSELL.ANALYTICS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/analytics-fr.png',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/analytics-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/analytics-nl.png',
    },
  },
  reports: {
    title: tKeys.UPSELL.REPORTS.TITLE,
    description: tKeys.UPSELL.REPORTS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/reports-fr.png',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/reports-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/reports-nl.png',
    },
  },
  mails: {
    title: tKeys.UPSELL.EMAILS.TITLE,
    description: tKeys.UPSELL.EMAILS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/mailbox-fr.png',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/mailbox-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/mailbox-nl.png',
    },
  },
  externalmailbox: {
    title: tKeys.UPSELL.EMAILS.TITLE,
    description: tKeys.UPSELL.EMAILS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/mailbox-fr.png',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/mailbox-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/mailbox-nl.png',
    },
  },
  aicalls: {
    title: tKeys.UPSELL.AI_CALLS.TITLE,
    description: tKeys.UPSELL.AI_CALLS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/ai-call-fr.png',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/ai-call-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/ai-call-nl.png',
    },
  },
  paymentplans: {
    title: tKeys.UPSELL.PAYMENT_PLANS.TITLE,
    description: tKeys.UPSELL.PAYMENT_PLANS.DESCRIPTION,
    mediaSrcByLocale: {
      fr: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/payment-plan-fr.png',
      en: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/payment-plan-en.png',
      nl: 'https://recovr-upsell-media.s3.eu-north-1.amazonaws.com/payment-plan-nl.png',
    },
  },
} as const satisfies Record<UpsellFeature, FeatureDefinitionWithLocales>;
